import { useState, useMemo } from 'react';
import { Card, Table, Button, Space, message, Modal, Tag } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { getApps, deleteApp } from '@/api/apps';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
import {
  AppstoreOutlined,
  AppstoreAddOutlined,
  EditOutlined,
  // DeleteOutlined,
} from '@ant-design/icons';
import type { AppInstance } from '@/types/app';

/**
 * App 列表页面（前端分页）
 */
export const AppList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();

  // ✅ 新增：管理每页显示条数的状态
  const [pageSize, setPageSize] = useState(20);

  const appQuery = useQuery({
    queryKey: ['apps', developerId],
    queryFn: getApps,
    enabled: !!developerId,
  });

  // 按 created_at 倒序排序（最新在前）
  const apps = useMemo(() => {
    if (!appQuery.data) return [];
    return [...appQuery.data].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [appQuery.data]);

  const loading = appQuery.isLoading;
  const error = appQuery.error;

  /**
   * 处理删除 App
   */
  const handleDelete = (appId: string, appName: string): void => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除 App "${appName}" 吗？此操作不可恢复。`,
      okType: 'danger',
      okText: '删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteApp(appId);
          message.success('App 删除成功');
          await queryClient.invalidateQueries({
            queryKey: ['apps', developerId],
          });
        } catch {
          message.error('删除失败，请重试。');
        }
      },
    });
  };
  console.log('apps', handleDelete);

  /**
   * 处理创建 App
   */
  const handleCreate = (): void => {
    navigate('/apps/create');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert
        message="加载失败"
        description="无法加载 App 列表，请检查网络或稍后重试。"
        className="mb-8"
      />
    );
  }

  // 表格列定义
  const columns: ColumnsType<AppInstance> = [
    {
      title: 'App 名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (text: string, record: AppInstance) => (
        <Link
          to={`/apps/${record.id}`}
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          {text}
        </Link>
      ),
    },
    {
      title: 'SDK 类别',
      dataIndex: 'sdk_category',
      key: 'sdk_category',
      width: 120,
      render: (category: string) => {
        const categoryMap: Record<string, { label: string; color: string }> = {
          smart_home: { label: '智能生活', color: 'blue' },
          wearable: { label: '可穿戴设备', color: 'purple' },
          healthcare: { label: '健康医疗', color: 'green' },
          industrial: { label: '工业 IoT', color: 'orange' },
        };
        const item = categoryMap[category] || { label: category, color: 'gray' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: '渠道标识符',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (channel: string) => {
        const channelMap: Record<string, { label: string; color: string }> = {
          official: { label: '官方渠道', color: 'primary' },
          beta: { label: '测试渠道', color: 'warning' },
          enterprise: { label: '企业定制', color: 'success' },
        };
        const item = channelMap[channel] || { label: channel, color: 'default' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: 'iOS Bundle ID',
      key: 'ios_bundle_id',
      ellipsis: true,
      render: (_: string, record: AppInstance) => {
        return record.ios_key?.bundle_id || <span className="text-gray-400">—</span>;
      },
    },
    {
      title: 'Android 包名',
      key: 'android_package_name',
      ellipsis: true,
      render: (_: string, record: AppInstance) => {
        return record.android_key?.bundle_id || <span className="text-gray-400">—</span>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => {
        return new Date(date).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).replace(/\//g, '-');
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record: AppInstance) => (
        <Space size="small">
          <Button
            size="small"
            type="default"
            icon={<EditOutlined className="text-sm" />}
            onClick={() => navigate(`/apps/${record.id}/edit`)}
            className="hover:bg-blue-50 hover:text-blue-600 transition-colors"
          >
            编辑
          </Button>
          {/* <Button
            size="small"
            danger
            icon={<DeleteOutlined className="text-sm" />}
            onClick={() => handleDelete(record.id, record.name)}
          >
            删除
          </Button> */}
        </Space>
      ),
    },
  ];

  // ✅ 关键：配置受控分页
  const paginationConfig: TablePaginationConfig = {
    pageSize: pageSize,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50'],
    showTotal: (total) => `共 ${total} 个App SDK`,
    onShowSizeChange: (_, size) => {
      setPageSize(size);
    },
    // 注意：因为是前端分页且只有一整页数据，不需要处理 onChange（页码切换）
    // 如果未来支持多页，才需要处理 current 和 onChange
  };

  return (
    <div className="w-full space-y-8">
      {/* 页面标题+操作区 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">App SDK 管理</h1>
          <p className="text-gray-500 mt-1">
            管理您创建的所有应用SDK，支持编辑、删除等操作。
          </p>
        </div>
        <Button
          type="primary"
          onClick={handleCreate}
          icon={<AppstoreAddOutlined />}
          className="px-6 hover:shadow-md transition-shadow"
        >
          创建 App SDK
        </Button>
      </div>

      {/* 表格卡片 */}
      <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300">
        <Table
          dataSource={apps}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={paginationConfig}
          scroll={{ x: 'max-content' }}
          bordered={false}
          size="middle"
          style={{
            '--ant-table-header-text-color': '#667085',
            '--ant-table-row-hover-bg': '#f5faff',
            '--ant-table-border-color': '#e8e8e8',
          } as React.CSSProperties}
          locale={{
            emptyText: (
              <div className="py-16 text-center">
                <AppstoreOutlined className="text-4xl text-gray-300 mb-4" />
                <div className="text-gray-500">暂无App SDK数据</div>
                <div className="text-gray-400 text-sm mt-2">
                  点击右上角「创建 App SDK」开始使用
                </div>
              </div>
            ),
          }}
        />
      </Card>
    </div>
  );
};